import { LANG_ENG, ENDPOINT_NEWS_ENG, ENDPOINT_NEWS_SV } from '../../constants/strings';

const InternalHyperLinks = [
  {
    "text": "LLM",
    "translation": "[LLM]",
    "id": "g0cSE4J3Jy"
  },
  {
    "text": "Gemini",
    "translation": "[Gemini]",
    "id": "rgbI9RMVdF"
  },
  {
    "text": "AI-agenter",
    "translation": "[AI-agenter]",
    "id": "AKfGaabrUb"
  },
  {
    "text": "Custom GPT",
    "translation": "[Custom GPT]",
    "id": "zMSm9aRGjk"
  },
  {
    "text": "Chatbot",
    "translation": "[Chatbot]",
    "id": "chatbot",
    "isDirectLink": true
  },
  {
    "text": "Claude",
    "translation": "[Claude]",
    "id": "claude",
    "isDirectLink": true
  },
  {
    "text": "Claude Cowork",
    "translation": "[Claude cowork]",
    "id": "16itJjoLlB"
  },
  {
    "text": "Claude Code",
    "translation": "[Claude code]",
    "id": "A1B2C3D4E5"
  },
  {
    "text": "Claude Design",
    "translation": "[Claude design]",
    "id": "XpLmNqRds"
  }
]

// Builds the full markdown link, e.g. "[Claude code](/ainyheter/A1B2C3D4E5)".
const buildMarkdownLink = (link, language) => {
  const path = link.isDirectLink
    ? `/${link.id}`
    : `${language === LANG_ENG ? ENDPOINT_NEWS_ENG : ENDPOINT_NEWS_SV}/${link.id}`;
  return `${link.translation}(${path})`;
};

// Ranges that must never be matched into: existing markdown links `[label](url)`
// and markdown headers, so we don't nest links or linkify a heading.
const findExistingLinkRanges = (text) => {
  const ranges = [];
  const linkRegex = /\[[^\]]*\]\([^)]*\)/g;
  let match;
  while ((match = linkRegex.exec(text))) {
    ranges.push([match.index, match.index + match[0].length]);
  }
  return ranges;
};

const findHeaderRanges = (text) => {
  const ranges = [];
  const headerRegex = /^#{1,6}[^\n]*$/gm;
  let match;
  while ((match = headerRegex.exec(text))) {
    ranges.push([match.index, match.index + match[0].length]);
  }
  return ranges;
};

const overlapsAny = (start, end, ranges) => ranges.some(([s, e]) => start < e && end > s);

const isLetter = (char) => !!char && /[a-zA-ZåäöÅÄÖ]/.test(char);

// All occurrences of `term` in `text` that fall outside excludedRanges and aren't
// glued to a surrounding word (so "Chatbot" won't match inside "Chatboten").
const findOccurrences = (text, term, excludedRanges) => {
  const occurrences = [];
  let index = text.indexOf(term);
  while (index !== -1) {
    const end = index + term.length;
    if (
      !isLetter(text[index - 1]) &&
      !isLetter(text[end]) &&
      !overlapsAny(index, end, excludedRanges)
    ) {
      occurrences.push([index, end]);
    }
    index = text.indexOf(term, index + 1);
  }
  return occurrences;
};

/**
 * Turns known terms into internal links, at most once per term per call.
 * When a term appears several times, the middle occurrence is linked so the
 * link doesn't cluster right next to the heading or get buried at the very end.
 *
 * `availableIds` (a Set of ids that exist in the *current rendering language's*
 * news data) filters out targets that only exist in the other language — e.g.
 * "Claude Code" and "Claude Cowork" are Swedish-only posts, so linking those
 * terms from English article bodies would point at a URL with no English
 * article behind it (a dead, un-indexable page). Direct links (chatbot, claude)
 * aren't news posts and are always valid.
 */
const linkifyText = (text, { currentId, language, availableIds } = {}) => {
  // Longest terms first, so "Claude Code" claims its range before "Claude" can.
  const candidates = InternalHyperLinks
    .filter(link => link.id !== currentId)
    .filter(link => link.isDirectLink || !availableIds || availableIds.has(link.id))
    .sort((a, b) => b.text.length - a.text.length);

  const excludedRanges = [...findHeaderRanges(text), ...findExistingLinkRanges(text)];
  const insertions = [];

  for (const link of candidates) {
    const occurrences = findOccurrences(text, link.text, excludedRanges);
    if (occurrences.length === 0) continue;

    const [start, end] = occurrences[Math.floor((occurrences.length - 1) / 2)];
    insertions.push({ start, end, replacement: buildMarkdownLink(link, language) });
    excludedRanges.push([start, end]);
  }

  insertions.sort((a, b) => b.start - a.start);
  return insertions.reduce(
    (result, { start, end, replacement }) => result.slice(0, start) + replacement + result.slice(end),
    text
  );
};

export { InternalHyperLinks, linkifyText };
